import { useState } from 'react';
import { Link } from 'react-router';

import {
  AsyncSection,
  DataTable,
  PageHeader,
  Panel,
  useNotifications,
  type Column,
} from '../../components';
import type { MachineClientResponse } from '../../api';
import { formatDateTime } from '../../format';
import { PATHS } from '../../routes/paths';
import { useMachineClients, useRevokeMachineClient, useRotateMachineClient } from '../queries';
import { CREDENTIALS_COPY, ROLE_LABELS, type RevealedSecret } from './credentials';

/**
 * The machine credentials that exist, and what can still be done to them.
 *
 * Issuing one has moved to [`NewCredentialPage`](./NewCredentialPage.tsx): the
 * screen a menu entry leads to says which integrations can call the API today,
 * and a form open above that answer is an invitation to type into it while
 * reading. Rotation and revocation stay here, because both are actions on a row
 * that already exists.
 *
 * **A rotated secret is shown once, and the screen says so before it is
 * dismissed.** It is returned by the rotation response and exists nowhere else
 * -- not in the list, not in the audit trail, not in our database in a readable
 * form. So it is rendered in a panel that has to be dismissed deliberately,
 * with the sentence that explains why, rather than in a toast that disappears
 * on a timer while somebody is finding their password manager. The issue screen
 * does the same with the secret it gets.
 */
export function StaffCredentialsPage() {
  const credentials = useMachineClients();
  const rotate = useRotateMachineClient();
  const revoke = useRevokeMachineClient();
  const { notify } = useNotifications();

  const [revealed, setRevealed] = useState<RevealedSecret | null>(null);

  const columns: readonly Column<MachineClientResponse>[] = [
    { key: 'name', header: 'Integração', render: (credential) => credential.name },
    {
      key: 'client_id',
      header: 'client_id',
      render: (credential) => <code>{credential.client_id}</code>,
    },
    {
      key: 'role',
      header: 'Permissão',
      render: (credential) => ROLE_LABELS[credential.role] ?? credential.role,
    },
    {
      key: 'status',
      header: 'Situação',
      render: (credential) =>
        credential.revoked_at !== null && credential.revoked_at !== undefined
          ? `Revogada em ${formatDateTime(credential.revoked_at)}`
          : 'Ativa',
    },
    {
      key: 'secret_rotated_at',
      header: 'Segredo trocado em',
      align: 'end',
      headerHint: 'Quando o segredo foi substituído pela última vez.',
      render: (credential) =>
        credential.secret_rotated_at !== null && credential.secret_rotated_at !== undefined
          ? formatDateTime(credential.secret_rotated_at)
          : '—',
    },
    {
      key: 'actions',
      header: 'Ações',
      align: 'end',
      render: (credential) => {
        if (credential.revoked_at !== null && credential.revoked_at !== undefined) {
          return <span className="table__muted">—</span>;
        }
        return (
          <span className="table__actions">
            <button
              type="button"
              className="button button--ghost"
              disabled={rotate.isPending}
              onClick={() => {
                rotate.mutate(credential.id, {
                  onSuccess: (rotated) => {
                    setRevealed({
                      clientId: rotated.client_id,
                      clientSecret: rotated.client_secret,
                    });
                    notify('success', 'Segredo substituído.');
                  },
                  onError: () => notify('error', 'Não foi possível trocar o segredo.'),
                });
              }}
            >
              Trocar segredo
            </button>
            <button
              type="button"
              className="button button--danger"
              disabled={revoke.isPending}
              onClick={() => {
                // A revogação não tem volta e vale na próxima requisição do
                // parceiro, então é a única ação desta tela que pergunta antes.
                const confirmed = window.confirm(
                  `Revogar ${credential.client_id}? A integração para de funcionar na próxima ` +
                    'requisição e não há como desfazer.',
                );
                if (!confirmed) {
                  return;
                }
                revoke.mutate(credential.id, {
                  onSuccess: () => notify('success', 'Credencial revogada.'),
                  onError: () => notify('error', 'Não foi possível revogar a credencial.'),
                });
              }}
            >
              Revogar
            </button>
          </span>
        );
      },
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={CREDENTIALS_COPY.title}
        description={CREDENTIALS_COPY.description}
        actions={
          <Link className="button button--primary" to={PATHS.staffCredentialNew}>
            {CREDENTIALS_COPY.issue}
          </Link>
        }
      />

      {revealed !== null && (
        <Panel
          title={CREDENTIALS_COPY.rotatedTitle}
          description={CREDENTIALS_COPY.secretDescription}
        >
          <dl className="definitions">
            <div>
              <dt>client_id</dt>
              <dd>
                <code>{revealed.clientId}</code>
              </dd>
            </div>
            <div>
              <dt>client_secret</dt>
              <dd>
                <code>{revealed.clientSecret}</code>
              </dd>
            </div>
          </dl>
          <button
            type="button"
            className="button button--primary"
            onClick={() => setRevealed(null)}
          >
            {CREDENTIALS_COPY.secretKept}
          </button>
        </Panel>
      )}

      <Panel title={CREDENTIALS_COPY.listTitle} description={CREDENTIALS_COPY.listDescription}>
        <AsyncSection
          state={credentials}
          loadingLabel={CREDENTIALS_COPY.loading}
          errorTitle={CREDENTIALS_COPY.loadError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={CREDENTIALS_COPY.empty}
          emptyDescription={CREDENTIALS_COPY.emptyDescription}
        >
          {(data) => (
            <DataTable
              id="machine-credentials"
              caption={CREDENTIALS_COPY.listCaption}
              columns={columns}
              rows={data.items}
              rowKey={(credential) => credential.id}
            />
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}
